import {$getSelection, $setSelection, LexicalEditor} from 'lexical';

/**
 * Clears Lexical's in-memory selection so that the next reconciliation does not
 * try to restore a DOM selection inside the contenteditable.  This is a
 * one-shot action — it prevents the *first* reconcile from stealing focus, but
 * cannot guard against subsequent editor updates.  Pair it with
 * `installEditorFocusGuard` for a durable solution.
 */
export const clearLiveEditorSelectionForCommentMode = (editor: LexicalEditor) => {
  editor.update(
    () => {
      if ($getSelection() !== null) {
        $setSelection(null);
      }
    },
    {discrete: true}
  );
};

/**
 * Installs a persistent focus guard on the editor root element.
 *
 * While active the guard intercepts `focus` events on the content editable and
 * immediately blurs them **unless** the focus was caused by user interaction
 * (a mousedown or keydown that occurred on the editor within the last frame).
 *
 * This lets users click into the editor to select text (mousedown sets the
 * `userInitiated` flag), while blocking programmatic focus caused by Lexical
 * reconciliation, remote Yjs collab updates, or other internal `editor.update`
 * calls.
 *
 * Returns a cleanup function that removes all listeners.
 */
export const installEditorFocusGuard = (editor: LexicalEditor): (() => void) => {
  const root = editor.getRootElement();
  if (!root) {
    return () => {};
  }

  let userInitiated = false;
  let resetTimer: ReturnType<typeof setTimeout> | null = null;

  const markUserInitiated = () => {
    userInitiated = true;
    if (resetTimer !== null) {
      clearTimeout(resetTimer);
    }
    // Reset after a tick — just long enough for the resulting focus event to fire.
    resetTimer = setTimeout(() => {
      userInitiated = false;
      resetTimer = null;
    }, 0);
  };

  const onFocus = () => {
    if (userInitiated) {
      return;
    }
    root.blur();
  };

  root.addEventListener('mousedown', markUserInitiated, true);
  root.addEventListener('keydown', markUserInitiated, true);
  root.addEventListener('focus', onFocus);

  return () => {
    root.removeEventListener('mousedown', markUserInitiated, true);
    root.removeEventListener('keydown', markUserInitiated, true);
    root.removeEventListener('focus', onFocus);
    if (resetTimer !== null) {
      clearTimeout(resetTimer);
    }
  };
};
